import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar, Box, Button, Checkbox, Container, Grid,
    FormControlLabel, Link, TextField, Typography, MenuItem,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import Copyright from 'components/common/Copyright';
import countries from 'config/countries';
import phones from 'config/phones';
import useAuth from 'hooks/useAuth';
import useTimeZones from 'hooks/useTimeZones';
import { CompanyData } from 'models/CompanyModel';
import { UserData } from 'models/UserModel';


const Register = () => {
    const {t} = useTranslation();
    const {register, requestInvite} = useAuth();
    const [step, setStep] = useState(0);
    const tz = useTimeZones();
    const [company, setCompany] = useState<CompanyData>({
        address: '',
        city: '',
        country: tz?.countries[0] || 'JP',
        email: '',
        exporterCode: '',
        fax: '',
        name: '',
        phone: tz ? `+${phones[tz.countries[0]]}-` : '',
        zipCode: '',
    });

    const start = () => setStep(0);

    const titles = [
        t('shipper_company_information'),
        t('sign_up'),
        t('request_invite'),
        t('success'),
        t('success'),
    ];

    const blocks = [
        <CompanyFields
            company={company}
            onFinish={v => {
                setStep(1);
                setCompany(v);
            }}
            onTypeChange={v => {
                setStep(2);
            }}
        />,
        <UserFields
            back={start}
            onFinish={user => register(company, user).then(() => setStep(3))}
        />,
        <DealerFields
            back={start}
            onFinish={(user: UserData) => requestInvite(user).then(() => setStep(4))}
        />,
        <FinishRegistration/>,
        <FinishInvite/>,
    ];

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar sx={{m: 1, bgcolor: 'secondary.main'}}>
                    <LockOutlined/>
                </Avatar>
                <Typography component="h1" variant="h5">
                    {titles[step]}
                </Typography>

                {blocks[step]}
            </Box>
            <Copyright sx={{mt: 8, mb: 4}}/>
        </Container>
    );
}

type CompanyFieldsProps = {
    company: CompanyData,
    onFinish: (v: CompanyData) => void,
    onTypeChange: (v: string) => void,
}

const CompanyFields = ({company, onFinish, onTypeChange}: CompanyFieldsProps) => {
    const {t} = useTranslation();
    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onFinish(Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as CompanyData);
    };

    return (
        <Box component="form" onSubmit={submit} sx={{mt: 3}}>
            <Grid container spacing={2} sx={{mb: 3}}>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        fullWidth
                        sx={{py: 2}}
                    >
                        <span>
                            {t('shipper')} <br/>
                        <small>{t('japanese_company')}</small>
                        </span>
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        variant="outlined"
                        fullWidth
                        sx={{py: 2}}
                        onClick={() => onTypeChange('dealer')}
                    >
                        <span>
                            {t('dealer')} <br/>
                        <small>{t('russian_client')}</small>
                        </span>
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                <TextField
                        autoComplete="organization"
                        name="name"
                        required
                        fullWidth
                        variant="outlined"
                        id="name"
                        label={t('company_name')}
                        autoFocus
                        defaultValue={company.name}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        type="email"
                        fullWidth
                        variant="outlined"
                        id="email"
                        label={t('company_email')}
                        name="email"
                        autoComplete="email"
                        defaultValue={company.email}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        id="exporterCode"
                        label={t('exporter_code')}
                        name="exporterCode"
                        defaultValue={company.exporterCode}
                    />
                </Grid>
                <Grid item xs={12}>
                    <hr/>
                </Grid>
                <Grid item xs={8} display="none">
                    <TextField
                        select
                        id="country"
                        name="country"
                        label={t('country')}
                        defaultValue={company.country}
                        fullWidth
                        variant="outlined"
                    >
                        {Object.entries(countries).map(([c, n]) => <MenuItem key={c} value={c}>{n}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        name="city"
                        label={t('prefecture_and_city')}
                        id="city"
                        autoComplete="address-level2"
                        defaultValue={company.city}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        name="zipCode"
                        label={t('zip_code')}
                        id="zipCode"
                        autoComplete="postal-code"
                        defaultValue={company.zipCode}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        name="address"
                        label={t('address')}
                        id="address"
                        autoComplete="street-address"
                        defaultValue={company.address}
                    />
                </Grid>
                <Grid item xs={12}>
                    <hr/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        variant="outlined"
                        name="phone"
                        label={t('phone')}
                        type="tel"
                        id="phone"
                        autoComplete="phone"
                        defaultValue={company.phone}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        name="fax"
                        label={t('fax')}
                        type="tel"
                        id="fax"
                        autoComplete="phone"
                        defaultValue={company.fax}
                    />
                </Grid>
            </Grid>
            <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{mt: 3, mb: 2}}
            >
                {t('next')}
            </Button>
            <Grid container justifyContent="flex-end">
                <Grid item>
                    <Link href="/login" variant="body2">
                        Already have an account? Sign in
                    </Link>
                </Grid>
            </Grid>
        </Box>
    )
}

type UserFieldsProps = {
    back: () => void,
    onFinish: (k: any) => void,
}

const UserFields = ({back, onFinish}: UserFieldsProps) => {
    const {t} = useTranslation();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onFinish(Object.fromEntries(new FormData(event.currentTarget).entries()));
    };

    return (
        <Box component="form" onSubmit={submit} sx={{mt: 3}}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        autoComplete="given-name"
                        name="firstName"
                        required
                        fullWidth
                        id="firstName"
                        label={t('first_name')}
                        autoFocus
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        id="lastName"
                        label={t('last_name')}
                        name="lastName"
                        autoComplete="family-name"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        type="email"
                        fullWidth
                        id="email"
                        label={t('email_address')}
                        name="email"
                        autoComplete="email"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        fullWidth
                        name="password"
                        label={t('password')}
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        inputProps={{minLength: 8}}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox value="allowExtraEmails" color="primary" required/>}
                        label={<>I read and agree with <Link href="/tos" variant="body2">
                            {t('terms_and_conditions')}
                        </Link></>}
                    />
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between" sx={{mt: 3, mb: 2}}>
                <Button
                    variant="text"
                    onClick={back}
                >
                    {t('back')}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{width: '50%'}}
                >
                    {t('sign_up')}
                </Button>
            </Grid>

            <Grid container justifyContent="flex-end">
                <Grid item>
                    <Link href="/login" variant="body2">
                        Already have an account? Sign in
                    </Link>
                </Grid>
            </Grid>
        </Box>
    )
};

const DealerFields = ({back, onFinish}: UserFieldsProps) => {
    const {t} = useTranslation();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onFinish(Object.fromEntries(new FormData(event.currentTarget).entries()));
    };

    return (
        <Box component="form" onSubmit={submit} sx={{mt: 3}}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        autoComplete="given-name"
                        name="firstName"
                        required
                        fullWidth
                        id="firstName"
                        label={t('first_name')}
                        autoFocus
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        fullWidth
                        id="lastName"
                        label={t('last_name')}
                        name="lastName"
                        autoComplete="family-name"
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        required
                        type="email"
                        fullWidth
                        id="email"
                        label={t('email_address')}
                        name="email"
                        autoComplete="email"
                    />
                </Grid>

            </Grid>
            <Grid container justifyContent="space-between" sx={{mt: 3, mb: 2}}>
                <Button
                    variant="text"
                    onClick={back}
                >
                    {t('back')}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{width: '50%'}}
                >
                    {t('send')}
                </Button>
            </Grid>

            <Grid container justifyContent="flex-end">
                <Grid item>
                    <Link href="/login" variant="body2">
                        Already have an account? Sign in
                    </Link>
                </Grid>
            </Grid>
        </Box>
    )
};

const FinishInvite = () => {
    const {t} = useTranslation();

    return (
        <Box sx={{mt: 3, textAlign: 'center'}}>
            <Typography mb={1}>
                Мы свяжемся с вами в ближайшее время
            </Typography>
        </Box>
    );
};

const FinishRegistration = () => {
    const {t} = useTranslation();

    return (
        <Box sx={{mt: 3, textAlign: 'center'}}>
            <Typography variant="h5" mb={1}>
                {t('registration_complete_title')}
            </Typography>
            <Typography mb={1}>
                {t('registration_complete_1')}
            </Typography>
            <Typography color="text.secondary" mb={1}>
                {t('registration_complete_2')}
            </Typography>
        </Box>
    )
};

export default Register;